import type React from 'react';
import type { FloatingContext } from '@floating-ui/react';

interface IJustifyOptions {
  arrowSize: number,
  floatingSize: number,
  elementsOffset: number,
  floatingOffset: number,
}

/**
 * Keeps the arrow inside the floating element.
 */
const justifyOffset = (value: number, options: IJustifyOptions): number => {
  const min = options.elementsOffset - options.floatingOffset;
  const max = options.floatingSize + options.floatingOffset - options.arrowSize;

  return Math.max(min, Math.min(value, max));
};

const calculateOffsets = (
  context: FloatingContext,
  arrowRef: React.MutableRefObject<HTMLElement>,
  arrowAnchorRef: React.MutableRefObject<HTMLElement>,
): { x: number, y: number } => {
  const { x = 0, y = 0 } = context.middlewareData.arrow ?? {};
  const arrowElement = arrowRef.current;
  const anchorElement = arrowAnchorRef?.current;

  /**
   * floating-ui positions the arrow against the reference itself; a custom anchor is calculated manually.
   */
  if (!arrowElement || !anchorElement) {
    return { x, y };
  }

  const isVertical = context.placement.startsWith('left') || context.placement.startsWith('right');
  const anchorRect = anchorElement.getBoundingClientRect();
  const arrowRect = arrowElement.getBoundingClientRect();
  const floatingRect = context.elements.floating.getBoundingClientRect();

  if (isVertical) {
    const elementsOffset = (Math.min(anchorRect.height, floatingRect.height) - arrowRect.height) / 2;

    return {
      x,
      y: justifyOffset(anchorRect.top - context.y + elementsOffset, {
        arrowSize: arrowRect.height,
        floatingSize: floatingRect.height,
        elementsOffset,
        floatingOffset: context.middlewareData.offset?.y ?? 0,
      }),
    };
  }

  const elementsOffset = (Math.min(anchorRect.width, floatingRect.width) - arrowRect.width) / 2;

  return {
    x: justifyOffset(anchorRect.left - context.x + elementsOffset, {
      arrowSize: arrowRect.width,
      floatingSize: floatingRect.width,
      elementsOffset,
      floatingOffset: context.middlewareData.offset?.x ?? 0,
    }),
    y,
  };
};

export const getArrowStyles = (
  context: FloatingContext,
  arrowRef: React.MutableRefObject<HTMLElement>,
  arrowAnchorRef: React.MutableRefObject<HTMLElement>,
): React.CSSProperties => {
  if (!context.middlewareData.arrow) {
    return {};
  }

  const isLeft = context.placement.startsWith('left');
  const isTop = context.placement.startsWith('top');
  const { x, y } = calculateOffsets(context, arrowRef, arrowAnchorRef);

  return {
    position: 'absolute',
    transform: `translateX(${ x }px) translateY(${ y }px)`,
    ...(isLeft ? { right: 0 } : { left: 0 }),
    ...(isTop ? { bottom: 0 } : { top: 0 }),
  };
};
